import { useEffect, useState } from 'react'

import { useAppKitWallet } from '@reown/appkit-wallet-button/react'
import { getWallet } from '@reown/web-wallet-core'

import { useAppKitContext } from '../../context/AppKitContext'

type UseWalletOptions = {
  redirectUrl?: string
}

type UseWalletResult = {
  wallet: Awaited<ReturnType<typeof getWallet>> | undefined
  error: string | undefined
  isInitializing: boolean
  isReady: boolean | undefined
}

export function useWallet(options?: UseWalletOptions): UseWalletResult {
  const { isReady, connect: connectWallet } = useAppKitWallet()
  const { appkit, wagmiConfig, appKitWalletButton } = useAppKitContext()
  const [wallet, setWallet] = useState<Awaited<ReturnType<typeof getWallet>> | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isInitializing, setIsInitializing] = useState(true)
  const redirectUrl = options?.redirectUrl || window.location.origin

  useEffect(() => {
    const initWallet = async () => {
      if (!appkit || !wagmiConfig || !isReady) return

      try {
        const walletInstance = await getWallet({
          appkit,
          appKitWalletButton: connectWallet,
          appKitUpdateEmail: appKitWalletButton.updateEmail,
          redirectUrl: redirectUrl + '/wc',
          wagmiConfig
        })

        if (!walletInstance) {
          setError('Failed to initialize wallet')
          return
        }

        setWallet(walletInstance)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize wallet')
      } finally {
        setIsInitializing(false)
      }
    }

    initWallet()
  }, [appkit, wagmiConfig, isReady, connectWallet, redirectUrl])

  return {
    wallet,
    error,
    isInitializing,
    isReady
  }
}
